import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AuthUser } from '@/types/auth';

interface SwaClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
  claims?: { typ: string; val: string }[];
}

export interface UseAuthReturn {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  login: () => void;
  logout: () => void;
}

const DEV_USER: AuthUser = {
  name: 'Dev User',
  email: 'dev@bosch.com',
  username: 'dev@bosch.com',
};

function getClaim(
  claims: { typ: string; val: string }[] | undefined,
  type: string,
): string | undefined {
  return claims?.find((c) => c.typ === type)?.val;
}

function toAuthUser(principal: SwaClientPrincipal): AuthUser {
  return {
    name: getClaim(principal.claims, 'name') ?? principal.userDetails,
    email:
      getClaim(
        principal.claims,
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
      ) ??
      getClaim(principal.claims, 'preferred_username') ??
      principal.userDetails,
    username: principal.userDetails,
  };
}

function useSwaAuth(): UseAuthReturn {
  const queryClient = useQueryClient();
  const [state, setState] = useState<{ isLoading: boolean; user: AuthUser | null }>({
    isLoading: true,
    user: null,
  });

  useEffect(() => {
    let cancelled = false;
    fetch('/.auth/me')
      .then((r) => {
        if (!r.ok) throw new Error('Auth check failed: ' + String(r.status));
        return r.json() as Promise<{ clientPrincipal: SwaClientPrincipal | null }>;
      })
      .then((data) => {
        if (!cancelled) {
          setState({
            isLoading: false,
            user: data.clientPrincipal ? toAuthUser(data.clientPrincipal) : null,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ isLoading: false, user: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(() => {
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `/.auth/login/aad?post_login_redirect_uri=${returnUrl}`;
  }, []);

  const logout = useCallback(() => {
    queryClient.clear();
    window.location.href = '/.auth/logout';
  }, [queryClient]);

  return {
    isAuthenticated: state.user != null,
    isLoading: state.isLoading,
    user: state.user,
    login,
    logout,
  };
}

function useDevAuth(): UseAuthReturn {
  const queryClient = useQueryClient();
  return {
    isAuthenticated: true,
    isLoading: false,
    user: DEV_USER,
    login: () => undefined,
    logout: () => {
      queryClient.clear();
    },
  };
}

export const useAuth = import.meta.env.DEV ? useDevAuth : useSwaAuth;
